import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ETIQUETAS_TIPO_ORGANISMO,
  TIPOS_ORGANISMO,
  esRutValido,
  formatearCLP,
  formatearPorcentajeSimple,
  formatearRut,
  type CrearOrganismoInput,
  type TipoOrganismo,
} from '@lexicore/shared';
import {
  IconoAlerta,
  IconoBasura,
  IconoBuscar,
  IconoCheck,
  IconoCerrar,
  IconoCopiar,
  IconoCuadricula,
  IconoEditar,
  IconoMas,
  IconoRayo,
  IconoRestablecer,
  IconoTabla,
} from '../componentes/Iconos';
import { Cargando, ErrorCarga, Insignia, Panel, TarjetaKpi, Vacio } from '../componentes/UI';
import { useAuth } from '../contextos/AuthContext';
import { useAvisos } from '../contextos/AvisosContext';
import {
  useOrganismo,
  useOrganismos,
  type ContactoOrganismo,
  type Organismo,
  type OrganismoDetalle,
} from '../lib/consultas';
import {
  useActualizarContactoOrganismo,
  useActualizarOrganismo,
  useCrearContactoOrganismo,
  useCrearOrganismo,
  useEliminarContactoOrganismo,
  useEliminarOrganismo,
} from '../lib/mutaciones';

type ModoVista = 'tarjetas' | 'tabla';
type FiltroDSO = 'TODOS' | 'RAPIDO' | 'PROMEDIO' | 'LENTO' | 'SIN_FACTURAS';
type CriterioOrden = 'nombre_asc' | 'nombre_desc' | 'monto_desc' | 'exito_desc' | 'licitaciones_desc' | 'dso_asc' | 'dso_desc';
type ChipRapido = 'TODOS' | 'PROYECTOS_ACTIVOS' | 'PAGADORES_LENTOS' | 'CON_MORA';

function obtenerSemaforoDSO(dias: number | null) {
  if (dias === null) {
    return {
      etiqueta: 'Sin facturas pagadas',
      color: 'var(--text-muted)',
      fondo: 'var(--bg-hover)',
      icono: '⚪',
      esLento: false,
    };
  }
  if (dias <= 35) {
    return {
      etiqueta: `Rápido Pagador (${Math.round(dias)}d)`,
      color: 'var(--accent-green)',
      fondo: 'var(--accent-green-soft)',
      icono: '🟢',
      esLento: false,
    };
  }
  if (dias <= 60) {
    return {
      etiqueta: `Plazo Normal (${Math.round(dias)}d)`,
      color: 'var(--accent-yellow)',
      fondo: 'var(--accent-yellow-soft)',
      icono: '🟡',
      esLento: false,
    };
  }
  return {
    etiqueta: `Pagador Lento (${Math.round(dias)}d)`,
    color: 'var(--accent-red)',
    fondo: 'var(--accent-red-soft)',
    icono: '🔴',
    esLento: true,
  };
}

function obtenerUrlWhatsApp(telefono?: string | null, organismoNombre?: string): string {
  if (!telefono) return '';
  const soloDigitos = telefono.replace(/\D/g, '');
  const conPrefijo = soloDigitos.startsWith('56') ? soloDigitos : `56${soloDigitos}`;
  const mensaje = encodeURIComponent(
    `Hola, te contacto desde Lexicore Labs respecto a nuestras gestiones con ${organismoNombre ?? 'su institución'}.`,
  );
  return `https://wa.me/${conPrefijo}?text=${mensaje}`;
}

export function Organismos() {
  const navegar = useNavigate();
  const avisos = useAvisos();
  const { puede } = useAuth();

  // Estados de Filtros
  const [busqueda, setBusqueda] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState<string>('TODOS');
  const [dsoFiltro, setDsoFiltro] = useState<FiltroDSO>('TODOS');
  const [chipRapido, setChipRapido] = useState<ChipRapido>('TODOS');
  const [orden, setOrden] = useState<CriterioOrden>('nombre_asc');
  const [vista, setVista] = useState<ModoVista>('tarjetas');

  // Modales
  const [modalOrganismoAbierto, setModalOrganismoAbierto] = useState(false);
  const [organismoAEditar, setOrganismoAEditar] = useState<Organismo | null>(null);

  const [organismoFichaId, setOrganismoFichaId] = useState<string | null>(null);
  const [organismoSeleccionado, setOrganismoSeleccionado] = useState<Organismo | null>(null);
  const [modalContactoAbierto, setModalContactoAbierto] = useState(false);
  const [contactoAEditar, setContactoAEditar] = useState<{ organismoId: string; contacto?: ContactoOrganismo } | null>(null);

  // Consultas y Mutaciones
  const { data, isLoading, isError, error, refetch } = useOrganismos(busqueda.trim() || undefined);
  const crearOrganismoMut = useCrearOrganismo();
  const actualizarOrganismoMut = useActualizarOrganismo();
  const eliminarOrganismoMut = useEliminarOrganismo();

  const organismos = data ?? [];

  // KPIs Consolidados Superiores
  const kpis = useMemo(() => {
    const total = organismos.length;
    let sumaMontoGanado = 0;
    let sumaDSO = 0;
    let cuentaDSO = 0;
    let sumaMora60d = 0;
    let sumaPendiente = 0;

    organismos.forEach((o) => {
      sumaMontoGanado += o.montoGanado || 0;
      if (o.diasPromedioPago !== null && o.diasPromedioPago > 0) {
        sumaDSO += o.diasPromedioPago;
        cuentaDSO++;
      }
      sumaMora60d += o.montoMora60d || 0;
      sumaPendiente += o.montoPendiente || 0;
    });

    const dsoPromedioGlobal = cuentaDSO > 0 ? Math.round(sumaDSO / cuentaDSO) : null;

    return {
      total,
      dsoPromedioGlobal,
      sumaMontoGanado,
      sumaMora60d,
      sumaPendiente,
    };
  }, [organismos]);

  // Filtrado y Ordenamiento Interactivo
  const organismosFiltrados = useMemo(() => {
    let resultado = [...organismos];

    // Filtro por Tipo de Organismo
    if (tipoFiltro !== 'TODOS') {
      resultado = resultado.filter((o) => o.tipo === tipoFiltro);
    }

    // Filtro por DSO
    if (dsoFiltro === 'RAPIDO') {
      resultado = resultado.filter((o) => o.diasPromedioPago !== null && o.diasPromedioPago <= 35);
    } else if (dsoFiltro === 'PROMEDIO') {
      resultado = resultado.filter(
        (o) => o.diasPromedioPago !== null && o.diasPromedioPago > 35 && o.diasPromedioPago <= 60,
      );
    } else if (dsoFiltro === 'LENTO') {
      resultado = resultado.filter((o) => o.diasPromedioPago !== null && o.diasPromedioPago > 60);
    } else if (dsoFiltro === 'SIN_FACTURAS') {
      resultado = resultado.filter((o) => o.diasPromedioPago === null);
    }

    // Chips Rápidos
    if (chipRapido === 'PROYECTOS_ACTIVOS') {
      resultado = resultado.filter((o) => (o.proyectosActivos || 0) > 0);
    } else if (chipRapido === 'PAGADORES_LENTOS') {
      resultado = resultado.filter((o) => o.diasPromedioPago !== null && o.diasPromedioPago > 60);
    } else if (chipRapido === 'CON_MORA') {
      resultado = resultado.filter((o) => (o.montoMora60d || 0) > 0 || (o.montoPendiente || 0) > 0);
    }

    // Ordenamiento
    resultado.sort((a, b) => {
      switch (orden) {
        case 'nombre_asc':
          return a.nombre.localeCompare(b.nombre, 'es');
        case 'nombre_desc':
          return b.nombre.localeCompare(a.nombre, 'es');
        case 'monto_desc':
          return (b.montoGanado || 0) - (a.montoGanado || 0);
        case 'exito_desc':
          return (b.tasaExitoPct ?? -1) - (a.tasaExitoPct ?? -1);
        case 'licitaciones_desc':
          return (b.totalLicitaciones || 0) - (a.totalLicitaciones || 0);
        case 'dso_asc': {
          const dsoA = a.diasPromedioPago ?? 9999;
          const dsoB = b.diasPromedioPago ?? 9999;
          return dsoA - dsoB;
        }
        case 'dso_desc': {
          const dsoA = a.diasPromedioPago ?? -1;
          const dsoB = b.diasPromedioPago ?? -1;
          return dsoB - dsoA;
        }
        default:
          return 0;
      }
    });

    return resultado;
  }, [organismos, tipoFiltro, dsoFiltro, chipRapido, orden]);

  const hayFiltrosActivos =
    busqueda.trim() !== '' ||
    tipoFiltro !== 'TODOS' ||
    dsoFiltro !== 'TODOS' ||
    chipRapido !== 'TODOS';

  const limpiarFiltros = () => {
    setBusqueda('');
    setTipoFiltro('TODOS');
    setDsoFiltro('TODOS');
    setChipRapido('TODOS');
  };

  const copiarDatosFacturacion = (org: Organismo | OrganismoDetalle) => {
    const texto = [
      'DATOS RECEPTOR FACTURA SII:',
      `Razón Social: ${org.nombre}`,
      `RUT: ${formatearRut(org.rut)}`,
      `Tipo Institucional: ${ETIQUETAS_TIPO_ORGANISMO[org.tipo as TipoOrganismo] ?? org.tipo}`,
      `Dirección: ${[org.direccion, org.comuna, org.region].filter(Boolean).join(', ') || 'No especificada'}`,
      org.sitioWeb ? `Sitio Web: ${org.sitioWeb}` : null,
    ]
      .filter(Boolean)
      .join('\n');

    void navigator.clipboard.writeText(texto).then(() => {
      avisos.exito(`Datos de facturación de ${org.nombre} copiados al portapapeles.`);
    });
  };

  const confirmarEliminar = async (org: Organismo) => {
    if (org.totalLicitaciones > 0) {
      avisos.error(
        `No es posible eliminar ${org.nombre} porque registra ${org.totalLicitaciones} licitaciones en su historial.`,
      );
      return;
    }
    if (!window.confirm(`¿Seguro que deseas eliminar el organismo "${org.nombre}"?`)) return;

    try {
      await eliminarOrganismoMut.mutateAsync(org.id);
      avisos.exito(`Organismo "${org.nombre}" eliminado correctamente.`);
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'Error al eliminar el organismo.');
    }
  };

  if (isError) return <ErrorCarga error={error} reintentar={() => void refetch()} />;

  return (
    <>
      {/* ── Encabezado Principal ── */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          marginBottom: 20,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 6, letterSpacing: '-0.02em' }}>
            Cartera de Organismos Públicos
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 680, lineHeight: 1.5 }}>
            Directorio de compradores institucionales de Mercado Público, comportamiento real de pago (DSO) y contrapartes clave para destrabar hitos y cobranzas.
          </p>
        </div>

        {puede('GESTOR') && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setOrganismoAEditar(null);
              setModalOrganismoAbierto(true);
            }}
          >
            <IconoMas size={15} />
            Nuevo Organismo
          </button>
        )}
      </div>

      {/* ── 4 Tarjetas de KPI Superiores ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 14,
          marginBottom: 24,
        }}
      >
        <TarjetaKpi
          etiqueta="Instituciones en Cartera"
          valor={kpis.total}
          acento="var(--accent-blue)"
          colorValor="var(--text-primary)"
          nota="Compradores públicos registrados"
        />

        <TarjetaKpi
          etiqueta="DSO Promedio Ponderado"
          valor={kpis.dsoPromedioGlobal !== null ? `${kpis.dsoPromedioGlobal} días` : '—'}
          acento={
            kpis.dsoPromedioGlobal === null
              ? 'var(--text-muted)'
              : kpis.dsoPromedioGlobal <= 35
                ? 'var(--accent-green)'
                : kpis.dsoPromedioGlobal <= 60
                  ? 'var(--accent-yellow)'
                  : 'var(--accent-red)'
          }
          colorValor={
            kpis.dsoPromedioGlobal === null
              ? 'var(--text-muted)'
              : kpis.dsoPromedioGlobal <= 35
                ? 'var(--accent-green)'
                : kpis.dsoPromedioGlobal <= 60
                  ? 'var(--accent-yellow)'
                  : 'var(--accent-red)'
          }
          nota={
            kpis.dsoPromedioGlobal !== null
              ? `Plazo legal de pago: 30 días (${kpis.dsoPromedioGlobal > 30 ? `+${kpis.dsoPromedioGlobal - 30}d desfase` : 'al día'})`
              : 'Sin facturas pagadas aún'
          }
        />

        <TarjetaKpi
          etiqueta="Total Histórico Ganado"
          valor={formatearCLP(kpis.sumaMontoGanado)}
          acento="var(--accent-green)"
          colorValor="var(--accent-green)"
          nota="Ingresos adjudicados con el Estado"
        />

        <TarjetaKpi
          etiqueta="Cartera en Mora (+60 días)"
          valor={formatearCLP(kpis.sumaMora60d)}
          acento={kpis.sumaMora60d > 0 ? 'var(--accent-red)' : 'var(--text-muted)'}
          colorValor={kpis.sumaMora60d > 0 ? 'var(--accent-red)' : 'var(--text-secondary)'}
          nota={
            kpis.sumaMora60d > 0
              ? 'Facturas vencidas que exigen cobranza activa'
              : `Total pendiente de cobro: ${formatearCLP(kpis.sumaPendiente)}`
          }
        />
      </div>

      {/* ── Barra de Búsqueda y Filtros ── */}
      <Panel style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Fila superior de filtros */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            {/* Buscador de texto */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '9px 14px',
                background: 'var(--bg-root)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                flex: '1 1 280px',
                minWidth: 240,
              }}
            >
              <IconoBuscar size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por nombre, RUT o comuna…"
                aria-label="Buscar organismo"
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: 'var(--text-primary)',
                  fontSize: 13,
                  fontFamily: 'var(--font-sans)',
                }}
              />
              {busqueda && (
                <button
                  type="button"
                  onClick={() => setBusqueda('')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  <IconoCerrar size={14} />
                </button>
              )}
            </div>

            {/* Selector de Tipo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label htmlFor="filtro-tipo" style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                Tipo:
              </label>
              <select
                id="filtro-tipo"
                value={tipoFiltro}
                onChange={(e) => setTipoFiltro(e.target.value)}
                style={{
                  padding: '8px 12px',
                  fontSize: 12.5,
                  background: 'var(--bg-root)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                <option value="TODOS">Todos los tipos ({organismos.length})</option>
                {TIPOS_ORGANISMO.map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {ETIQUETAS_TIPO_ORGANISMO[tipo]}
                  </option>
                ))}
              </select>
            </div>

            {/* Selector de Comportamiento DSO */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label htmlFor="filtro-dso" style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                Pago:
              </label>
              <select
                id="filtro-dso"
                value={dsoFiltro}
                onChange={(e) => setDsoFiltro(e.target.value as FiltroDSO)}
                style={{
                  padding: '8px 12px',
                  fontSize: 12.5,
                  background: 'var(--bg-root)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                <option value="TODOS">Cualquier plazo</option>
                <option value="RAPIDO">🟢 Rápido Pagador (≤ 35 días)</option>
                <option value="PROMEDIO">🟡 Plazo Normal (36 - 60 días)</option>
                <option value="LENTO">🔴 Pagador Lento (&gt; 60 días)</option>
                <option value="SIN_FACTURAS">⚪ Sin facturas pagadas</option>
              </select>
            </div>

            {/* Selector de Orden */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label htmlFor="filtro-orden" style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                Ordenar:
              </label>
              <select
                id="filtro-orden"
                value={orden}
                onChange={(e) => setOrden(e.target.value as CriterioOrden)}
                style={{
                  padding: '8px 12px',
                  fontSize: 12.5,
                  background: 'var(--bg-root)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                <option value="nombre_asc">Nombre (A → Z)</option>
                <option value="nombre_desc">Nombre (Z → A)</option>
                <option value="monto_desc">Mayor Monto Ganado</option>
                <option value="exito_desc">Mayor Tasa de Éxito</option>
                <option value="licitaciones_desc">Más Licitaciones</option>
                <option value="dso_asc">DSO: Más rápido primero</option>
                <option value="dso_desc">DSO: Más lento primero</option>
              </select>
            </div>

            {/* Toggle Vista: Tarjetas vs Tabla */}
            <div
              style={{
                display: 'flex',
                background: 'var(--bg-root)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                padding: 2,
              }}
            >
              <button
                type="button"
                onClick={() => setVista('tarjetas')}
                title="Vista en tarjetas ejecutivas"
                style={{
                  background: vista === 'tarjetas' ? 'var(--bg-card)' : 'transparent',
                  border: 'none',
                  color: vista === 'tarjetas' ? 'var(--text-primary)' : 'var(--text-muted)',
                  padding: '6px 10px',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <IconoCuadricula size={15} />
              </button>
              <button
                type="button"
                onClick={() => setVista('tabla')}
                title="Vista en tabla compacta"
                style={{
                  background: vista === 'tabla' ? 'var(--bg-card)' : 'transparent',
                  border: 'none',
                  color: vista === 'tabla' ? 'var(--text-primary)' : 'var(--text-muted)',
                  padding: '6px 10px',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <IconoTabla size={15} />
              </button>
            </div>
          </div>

          {/* Fila de Chips Rápidos de 1 Clic */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              paddingTop: 8,
              borderTop: '1px solid var(--border-subtle)',
            }}
          >
            <span style={{ fontSize: 11.5, color: 'var(--text-muted)', marginRight: 4 }}>
              Filtros rápidos:
            </span>

            <button
              type="button"
              onClick={() => setChipRapido('TODOS')}
              className={`btn btn-sm ${chipRapido === 'TODOS' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ padding: '4px 10px', fontSize: 11.5 }}
            >
              Todos ({organismos.length})
            </button>

            <button
              type="button"
              onClick={() => setChipRapido('PROYECTOS_ACTIVOS')}
              className={`btn btn-sm ${chipRapido === 'PROYECTOS_ACTIVOS' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ padding: '4px 10px', fontSize: 11.5 }}
            >
              <IconoRayo size={13} />
              Con Proyectos Activos
            </button>

            <button
              type="button"
              onClick={() => setChipRapido('PAGADORES_LENTOS')}
              className={`btn btn-sm ${chipRapido === 'PAGADORES_LENTOS' ? 'btn-primary' : 'btn-secondary'}`}
              style={{
                padding: '4px 10px',
                fontSize: 11.5,
                borderColor: chipRapido === 'PAGADORES_LENTOS' ? undefined : 'var(--accent-red-glow)',
              }}
            >
              🔴 Pagadores Lentos (DSO &gt; 60d)
            </button>

            <button
              type="button"
              onClick={() => setChipRapido('CON_MORA')}
              className={`btn btn-sm ${chipRapido === 'CON_MORA' ? 'btn-primary' : 'btn-secondary'}`}
              style={{
                padding: '4px 10px',
                fontSize: 11.5,
                borderColor: chipRapido === 'CON_MORA' ? undefined : 'var(--accent-yellow-soft)',
              }}
            >
              <IconoAlerta size={13} />
              Con Facturas por Cobrar
            </button>

            {hayFiltrosActivos && (
              <button
                type="button"
                onClick={limpiarFiltros}
                className="btn btn-secondary btn-sm"
                style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: 11.5 }}
              >
                <IconoRestablecer size={13} />
                Limpiar filtros
              </button>
            )}
          </div>
        </div>
      </Panel>

      {/* ── Contenido Principal (Lista / Grilla) ── */}
      {isLoading ? (
        <Cargando texto="Cargando directorio de compradores públicos…" />
      ) : organismosFiltrados.length === 0 ? (
        <Panel>
          <Vacio
            titulo={hayFiltrosActivos ? 'Ningún organismo coincide con los filtros aplicados' : 'Todavía no hay organismos registrados'}
            detalle={
              hayFiltrosActivos
                ? 'Prueba modificando el texto de búsqueda o los selectores de tipo y plazo.'
                : 'Los organismos se crean automáticamente al importar licitaciones o puedes registrarlos con el botón "+ Nuevo Organismo".'
            }
            accion={
              hayFiltrosActivos ? (
                <button type="button" className="btn btn-secondary btn-sm" onClick={limpiarFiltros}>
                  Restablecer filtros
                </button>
              ) : puede('GESTOR') ? (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    setOrganismoAEditar(null);
                    setModalOrganismoAbierto(true);
                  }}
                >
                  <IconoMas size={14} />
                  Crear primer organismo
                </button>
              ) : undefined
            }
          />
        </Panel>
      ) : vista === 'tarjetas' ? (
        /* ── Vista de Tarjetas Ejecutivas ── */
        <div className="proyectos-grid">
          {organismosFiltrados.map((organismo) => {
            const semaforo = obtenerSemaforoDSO(organismo.diasPromedioPago);
            const contactoPrincipal = organismo.contactos && organismo.contactos.length > 0 ? organismo.contactos[0] : null;

            return (
              <article
                key={organismo.id}
                className="proyecto-card"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: 14,
                  cursor: 'default',
                  borderTop: (organismo.proyectosActivos || 0) > 0 ? '3px solid var(--accent-blue)' : undefined,
                }}
              >
                <div>
                  {/* Encabezado: Nombre y Tipo */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 10,
                      marginBottom: 8,
                    }}
                  >
                    <div>
                      <h3
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          lineHeight: 1.3,
                          marginBottom: 4,
                          color: 'var(--text-primary)',
                        }}
                      >
                        {organismo.nombre}
                      </h3>
                      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                        RUT: <strong>{formatearRut(organismo.rut)}</strong>
                        {organismo.comuna && ` · ${organismo.comuna}`}
                      </div>
                    </div>

                    <span className="badge badge-cat" style={{ flexShrink: 0 }}>
                      {ETIQUETAS_TIPO_ORGANISMO[organismo.tipo as TipoOrganismo] ?? organismo.tipo}
                    </span>
                  </div>

                  {/* Semáforo de DSO y Proyectos Activos */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap',
                      marginBottom: 14,
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '3px 8px',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 11,
                        fontWeight: 600,
                        background: semaforo.fondo,
                        color: semaforo.color,
                        border: `1px solid ${semaforo.color}33`,
                      }}
                      title={
                        organismo.sobreplazoDias
                          ? `Sobreplazo respecto a condición pactada: +${organismo.sobreplazoDias} días`
                          : 'Comportamiento calculado con facturas pagadas'
                      }
                    >
                      {semaforo.icono} {semaforo.etiqueta}
                      {organismo.sobreplazoDias && organismo.sobreplazoDias > 0 && (
                        <span style={{ opacity: 0.85, fontSize: 10 }}>
                          (+{organismo.sobreplazoDias}d sobreplazo)
                        </span>
                      )}
                    </span>

                    {(organismo.proyectosActivos || 0) > 0 && (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '3px 8px',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: 11,
                          fontWeight: 600,
                          background: 'var(--accent-blue-soft)',
                          color: 'var(--accent-blue)',
                          border: '1px solid rgba(33, 150, 243, 0.25)',
                        }}
                      >
                        <IconoRayo size={12} />
                        {organismo.proyectosActivos}{' '}
                        {organismo.proyectosActivos === 1 ? 'Proyecto Activo' : 'Proyectos Activos'}
                      </span>
                    )}
                  </div>

                  {/* Métricas Comerciales */}
                  <div
                    style={{
                      background: 'var(--bg-root)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 'var(--radius-sm)',
                      padding: '10px 12px',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: 8,
                      marginBottom: 14,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Licitaciones</div>
                      <div style={{ fontSize: 12.5, fontWeight: 700 }}>
                        <span style={{ color: 'var(--accent-green)' }}>{organismo.adjudicadas}</span> / {organismo.totalLicitaciones}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Tasa de Éxito</div>
                      <div
                        style={{
                          fontSize: 12.5,
                          fontWeight: 700,
                          color:
                            organismo.tasaExitoPct === null
                              ? 'var(--text-muted)'
                              : organismo.tasaExitoPct >= 50
                                ? 'var(--accent-green)'
                                : 'var(--text-primary)',
                        }}
                      >
                        {organismo.tasaExitoPct === null ? '—' : formatearPorcentajeSimple(organismo.tasaExitoPct)}
                      </div>
                    </div>

                    <div style={{ gridColumn: 'span 2' }}>
                      <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Monto Ganado Histórico</div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--accent-green)' }}>
                        {formatearCLP(organismo.montoGanado)}
                      </div>
                    </div>
                  </div>

                  {/* Contraparte Principal */}
                  <div
                    style={{
                      padding: '8px 10px',
                      background: 'var(--bg-surface)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 12,
                    }}
                  >
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>
                      Contraparte Principal:
                    </div>
                    {contactoPrincipal ? (
                      <div>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                          {contactoPrincipal.nombre}
                          {contactoPrincipal.cargo && (
                            <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 11 }}>
                              {' '}
                              · {contactoPrincipal.cargo}
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            marginTop: 4,
                            fontSize: 11,
                            color: 'var(--text-secondary)',
                            flexWrap: 'wrap',
                          }}
                        >
                          {contactoPrincipal.telefono && (
                            <span>📞 {contactoPrincipal.telefono}</span>
                          )}
                          {contactoPrincipal.email && (
                            <span title={contactoPrincipal.email}>✉️ {contactoPrincipal.email}</span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: 11 }}>
                        Sin contactos registrados
                      </div>
                    )}
                  </div>
                </div>

                {/* Botones de Acción de 1 Clic */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    paddingTop: 12,
                    borderTop: '1px solid var(--border-subtle)',
                    flexWrap: 'wrap',
                  }}
                >
                  {/* WhatsApp directo si hay teléfono */}
                  {contactoPrincipal?.telefono ? (
                    <a
                      href={obtenerUrlWhatsApp(contactoPrincipal.telefono, organismo.nombre)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary btn-sm"
                      style={{
                        padding: '5px 8px',
                        fontSize: 11.5,
                        color: '#25D366',
                        borderColor: 'rgba(37, 211, 102, 0.3)',
                      }}
                      title="Abrir WhatsApp directo con la contraparte"
                    >
                      💬 WhatsApp
                    </a>
                  ) : null}

                  {/* Copiar datos de facturación SII */}
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    style={{ padding: '5px 8px', fontSize: 11.5 }}
                    onClick={() => copiarDatosFacturacion(organismo)}
                    title="Copiar datos que exige el SII para emitir factura"
                  >
                    <IconoCopiar size={13} />
                    Copiar Datos
                  </button>

                  {/* Lanzar nueva oferta preseleccionada */}
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    style={{ padding: '5px 8px', fontSize: 11.5 }}
                    onClick={() => navegar(`/nueva-oferta?organismoId=${organismo.id}`)}
                    title="Crear oferta técnica preseleccionando este organismo"
                  >
                    <IconoRayo size={13} style={{ color: 'var(--accent-yellow)' }} />
                    Nueva Oferta
                  </button>

                  {/* Editar Organismo */}
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    style={{ padding: '5px 8px', fontSize: 11.5 }}
                    onClick={() => {
                      setOrganismoAEditar(organismo);
                      setModalOrganismoAbierto(true);
                    }}
                    title="Editar datos del organismo"
                  >
                    <IconoEditar size={13} />
                    Editar
                  </button>

                  {/* Ver Ficha Detallada */}
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    style={{ padding: '5px 10px', fontSize: 11.5, marginLeft: 'auto' }}
                    onClick={() => {
                      setOrganismoSeleccionado(organismo);
                      setOrganismoFichaId(organismo.id);
                    }}
                  >
                    Ver Ficha
                  </button>

                  {/* Eliminar (solo si no tiene licitaciones y es admin) */}
                  {puede('ADMIN') && organismo.totalLicitaciones === 0 && (
                    <button
                      type="button"
                      className="btn-icono"
                      onClick={() => void confirmarEliminar(organismo)}
                      title="Eliminar organismo sin historial"
                      style={{ color: 'var(--accent-red)' }}
                    >
                      <IconoBasura size={14} />
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        /* ── Vista de Tabla Compacta ── */
        <Panel>
          <div className="tabla-contenedor" style={{ overflowX: 'auto' }}>
            <table className="tabla">
              <thead>
                <tr>
                  <th>Organismo & RUT</th>
                  <th>Tipo</th>
                  <th>Comportamiento Pago (DSO)</th>
                  <th>Proyectos</th>
                  <th style={{ textAlign: 'right' }}>Licitaciones</th>
                  <th style={{ textAlign: 'right' }}>Tasa Éxito</th>
                  <th style={{ textAlign: 'right' }}>Monto Ganado</th>
                  <th>Contraparte</th>
                  <th style={{ textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {organismosFiltrados.map((org) => {
                  const semaforo = obtenerSemaforoDSO(org.diasPromedioPago);
                  const contactoPrincipal = org.contactos && org.contactos.length > 0 ? org.contactos[0] : null;

                  return (
                    <tr key={org.id}>
                      <td>
                        <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{org.nombre}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                          {formatearRut(org.rut)}
                          {org.comuna && ` · ${org.comuna}`}
                        </div>
                      </td>
                      <td>
                        <span className="badge badge-cat">
                          {ETIQUETAS_TIPO_ORGANISMO[org.tipo as TipoOrganismo] ?? org.tipo}
                        </span>
                      </td>
                      <td>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '2px 6px',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 11,
                            fontWeight: 600,
                            background: semaforo.fondo,
                            color: semaforo.color,
                          }}
                        >
                          {semaforo.icono} {semaforo.etiqueta}
                        </span>
                      </td>
                      <td>
                        {(org.proyectosActivos || 0) > 0 ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              fontSize: 11,
                              fontWeight: 700,
                              color: 'var(--accent-blue)',
                            }}
                          >
                            <IconoRayo size={12} />
                            {org.proyectosActivos} activos
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>
                        <span style={{ color: 'var(--accent-green)' }}>{org.adjudicadas}</span> / {org.totalLicitaciones}
                      </td>
                      <td
                        style={{
                          textAlign: 'right',
                          fontWeight: 700,
                          color:
                            org.tasaExitoPct === null
                              ? 'var(--text-muted)'
                              : org.tasaExitoPct >= 50
                                ? 'var(--accent-green)'
                                : 'var(--text-primary)',
                        }}
                      >
                        {org.tasaExitoPct === null ? '—' : formatearPorcentajeSimple(org.tasaExitoPct)}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 800, color: 'var(--accent-green)' }}>
                        {formatearCLP(org.montoGanado)}
                      </td>
                      <td>
                        {contactoPrincipal ? (
                          <div style={{ fontSize: 11.5 }}>
                            <div style={{ fontWeight: 600 }}>{contactoPrincipal.nombre}</div>
                            <div style={{ color: 'var(--text-muted)' }}>{contactoPrincipal.telefono || contactoPrincipal.email}</div>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            style={{ padding: '4px 8px', fontSize: 11 }}
                            onClick={() => copiarDatosFacturacion(org)}
                            title="Copiar datos SII"
                          >
                            <IconoCopiar size={12} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            style={{ padding: '4px 8px', fontSize: 11 }}
                            onClick={() => {
                              setOrganismoAEditar(org);
                              setModalOrganismoAbierto(true);
                            }}
                            title="Editar datos del organismo"
                          >
                            <IconoEditar size={12} />
                            Editar
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            style={{ padding: '4px 8px', fontSize: 11 }}
                            onClick={() => {
                              setOrganismoSeleccionado(org);
                              setOrganismoFichaId(org.id);
                            }}
                          >
                            Ficha
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {/* ── Modal Ficha Detallada Bimodal ── */}
      {organismoFichaId && (
        <ModalFichaOrganismo
          organismoId={organismoFichaId}
          organismoInicial={organismoSeleccionado ?? organismos.find((o) => o.id === organismoFichaId)}
          alCerrar={() => {
            setOrganismoFichaId(null);
            setOrganismoSeleccionado(null);
          }}
          alAbrirEditarOrganismo={(org) => {
            setOrganismoAEditar(org);
            setModalOrganismoAbierto(true);
          }}
          alAbrirCrearContacto={() => {
            setContactoAEditar({ organismoId: organismoFichaId });
            setModalContactoAbierto(true);
          }}
          alAbrirEditarContacto={(contacto) => {
            setContactoAEditar({ organismoId: organismoFichaId, contacto });
            setModalContactoAbierto(true);
          }}
          onCopiarDatos={copiarDatosFacturacion}
        />
      )}

      {/* ── Modal Crear / Editar Organismo ── */}
      {modalOrganismoAbierto && (
        <ModalFormOrganismo
          organismo={organismoAEditar}
          alCerrar={() => {
            setModalOrganismoAbierto(false);
            setOrganismoAEditar(null);
          }}
          guardando={crearOrganismoMut.isPending || actualizarOrganismoMut.isPending}
          alGuardar={async (datos) => {
            try {
              if (organismoAEditar) {
                await actualizarOrganismoMut.mutateAsync({ id: organismoAEditar.id, datos });
                avisos.exito(`Organismo "${datos.nombre}" actualizado con éxito.`);
                if (organismoFichaId === organismoAEditar.id) {
                  setOrganismoSeleccionado((prev) => (prev ? ({ ...prev, ...datos } as Organismo) : null));
                }
              } else {
                await crearOrganismoMut.mutateAsync(datos as CrearOrganismoInput);
                avisos.exito(`Organismo "${datos.nombre}" creado exitosamente.`);
              }
              setModalOrganismoAbierto(false);
              setOrganismoAEditar(null);
            } catch (err) {
              avisos.error(err instanceof Error ? err.message : 'Error al guardar el organismo.');
            }
          }}
        />
      )}

      {/* ── Modal Crear / Editar Contacto ── */}
      {modalContactoAbierto && contactoAEditar && (
        <ModalFormContacto
          organismoId={contactoAEditar.organismoId}
          contacto={contactoAEditar.contacto}
          alCerrar={() => {
            setModalContactoAbierto(false);
            setContactoAEditar(null);
          }}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Subcomponente: Modal Ficha Detallada Bimodal
// ─────────────────────────────────────────────────────────────

interface PropsFicha {
  organismoId: string;
  organismoInicial?: Organismo;
  alCerrar: () => void;
  alAbrirEditarOrganismo: (org: Organismo | OrganismoDetalle) => void;
  alAbrirCrearContacto: () => void;
  alAbrirEditarContacto: (contacto: ContactoOrganismo) => void;
  onCopiarDatos: (org: OrganismoDetalle | Organismo) => void;
}

function ModalFichaOrganismo({
  organismoId,
  organismoInicial,
  alCerrar,
  alAbrirEditarOrganismo,
  alAbrirCrearContacto,
  alAbrirEditarContacto,
  onCopiarDatos,
}: PropsFicha) {
  const avisos = useAvisos();
  const navegar = useNavigate();
  const { data: orgDetalle, isLoading, isError, error, refetch } = useOrganismo(organismoId);
  const eliminarContactoMut = useEliminarContactoOrganismo();

  const [pestaña, setPestaña] = useState<'comercial' | 'financiera'>('comercial');

  // Objeto base garantizado con datos iniciales si existen
  const base: Organismo | OrganismoDetalle | null =
    orgDetalle ??
    (organismoInicial
      ? ({
          ...organismoInicial,
          contactos: organismoInicial.contactos ?? [],
          licitaciones: [],
          proyectos: [],
          facturas: [],
          metricas: {
            totalLicitaciones: organismoInicial.totalLicitaciones,
            adjudicadas: organismoInicial.adjudicadas,
            perdidas: organismoInicial.perdidas,
            tasaExitoPct: organismoInicial.tasaExitoPct,
            montoGanado: organismoInicial.montoGanado,
            ticketPromedio:
              organismoInicial.adjudicadas > 0
                ? organismoInicial.montoGanado / organismoInicial.adjudicadas
                : null,
            proyectosActivos: organismoInicial.proyectosActivos,
            totalFacturado: 0,
            totalCobrado: 0,
            saldoPendiente: organismoInicial.montoPendiente,
            mora60d: organismoInicial.montoMora60d,
            diasPromedioPago: organismoInicial.diasPromedioPago,
            sobreplazoDias: organismoInicial.sobreplazoDias,
            facturasPagadas: organismoInicial.facturasPagadas,
          },
        } as unknown as OrganismoDetalle)
      : null);

  if (isLoading && !base) {
    return (
      <div
        className="modal-overlay"
        onClick={(e) => e.target === e.currentTarget && alCerrar()}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal" style={{ maxWidth: 760 }}>
          <Cargando texto="Cargando ficha 360° del organismo…" />
        </div>
      </div>
    );
  }

  if (isError && !base) {
    return (
      <div
        className="modal-overlay"
        onClick={(e) => e.target === e.currentTarget && alCerrar()}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal" style={{ maxWidth: 760 }}>
          <ErrorCarga error={error} reintentar={() => void refetch()} />
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={alCerrar}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!base) return null;

  const metricas = (base as OrganismoDetalle).metricas ?? {
    totalLicitaciones: base.totalLicitaciones ?? 0,
    adjudicadas: base.adjudicadas ?? 0,
    perdidas: base.perdidas ?? 0,
    tasaExitoPct: base.tasaExitoPct ?? null,
    montoGanado: base.montoGanado ?? 0,
    ticketPromedio: null,
    proyectosActivos: base.proyectosActivos ?? 0,
    totalFacturado: 0,
    totalCobrado: 0,
    saldoPendiente: base.montoPendiente ?? 0,
    mora60d: base.montoMora60d ?? 0,
    diasPromedioPago: base.diasPromedioPago ?? null,
    sobreplazoDias: base.sobreplazoDias ?? null,
    facturasPagadas: base.facturasPagadas ?? 0,
  };

  const contactos = (base as OrganismoDetalle).contactos ?? base.contactos ?? [];
  const licitaciones = (base as OrganismoDetalle).licitaciones ?? [];
  const proyectos = (base as OrganismoDetalle).proyectos ?? [];
  const facturas = (base as OrganismoDetalle).facturas ?? [];

  const semaforo = obtenerSemaforoDSO(metricas.diasPromedioPago);

  const eliminarContacto = async (contacto: ContactoOrganismo) => {
    if (!window.confirm(`¿Eliminar el contacto "${contacto.nombre}"?`)) return;
    try {
      await eliminarContactoMut.mutateAsync(contacto.id);
      avisos.exito(`Contacto "${contacto.nombre}" eliminado.`);
      void refetch();
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'Error al eliminar contacto.');
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) alCerrar();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="modal" style={{ maxWidth: 940, maxHeight: '92vh', overflowY: 'auto' }}>
        {/* Cabecera del Modal */}
        <div className="modal-header" style={{ alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
              <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>{base.nombre}</h2>
              <span className="badge badge-cat">
                {ETIQUETAS_TIPO_ORGANISMO[base.tipo as TipoOrganismo] ?? base.tipo}
              </span>
              {isLoading && (
                <span
                  className="badge"
                  style={{
                    fontSize: 11,
                    background: 'var(--accent-blue-soft)',
                    color: 'var(--accent-blue)',
                    border: '1px solid rgba(33, 150, 243, 0.3)',
                  }}
                >
                  Sincronizando historial…
                </span>
              )}
            </div>

            <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <span>
                RUT: <strong style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{formatearRut(base.rut)}</strong>
              </span>
              {(base.comuna || base.region) && (
                <span>📍 {[base.direccion, base.comuna, base.region].filter(Boolean).join(', ')}</span>
              )}
              {base.sitioWeb && (
                <a
                  href={base.sitioWeb}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent-blue)', textDecoration: 'none' }}
                >
                  🌐 Sitio Web
                </a>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => alAbrirEditarOrganismo(base)}
              title="Editar datos institucionales del organismo"
            >
              <IconoEditar size={13} />
              Editar Organismo
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => onCopiarDatos(base)}
              title="Copiar datos que exige el SII para facturar"
            >
              <IconoCopiar size={13} />
              Copiar Datos SII
            </button>
            <button type="button" className="btn-icono" onClick={alCerrar} aria-label="Cerrar modal">
              <IconoCerrar size={16} />
            </button>
          </div>
        </div>

        {/* Selector de Pestañas Bimodal */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid var(--border-subtle)',
            padding: '0 24px',
            background: 'var(--bg-root)',
            gap: 20,
          }}
        >
          <button
            type="button"
            onClick={() => setPestaña('comercial')}
            style={{
              background: 'none',
              border: 'none',
              borderBottom: pestaña === 'comercial' ? '2px solid var(--accent-blue)' : '2px solid transparent',
              color: pestaña === 'comercial' ? 'var(--text-primary)' : 'var(--text-muted)',
              fontWeight: pestaña === 'comercial' ? 700 : 500,
              padding: '12px 4px',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            💼 Inteligencia Comercial & Contrapartes
          </button>

          <button
            type="button"
            onClick={() => setPestaña('financiera')}
            style={{
              background: 'none',
              border: 'none',
              borderBottom: pestaña === 'financiera' ? '2px solid var(--accent-green)' : '2px solid transparent',
              color: pestaña === 'financiera' ? 'var(--text-primary)' : 'var(--text-muted)',
              fontWeight: pestaña === 'financiera' ? 700 : 500,
              padding: '12px 4px',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            💰 Comportamiento Financiero & Cobranza
          </button>
        </div>

        {/* Cuerpo del Modal */}
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: 24 }}>
          {pestaña === 'comercial' ? (
            /* ── PESTAÑA A: COMERCIAL & CONTRAPARTES ── */
            <>
              {/* Tarjetas de Métricas Comerciales */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
                <div style={{ background: 'var(--bg-card)', padding: '12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Licitaciones Totales</div>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>{metricas.totalLicitaciones}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-secondary)' }}>
                    Ganadas: {metricas.adjudicadas} · Perdidas: {metricas.perdidas}
                  </div>
                </div>

                <div style={{ background: 'var(--bg-card)', padding: '12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Tasa de Éxito</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: metricas.tasaExitoPct !== null && metricas.tasaExitoPct >= 50 ? 'var(--accent-green)' : 'var(--text-primary)' }}>
                    {metricas.tasaExitoPct === null ? '—' : formatearPorcentajeSimple(metricas.tasaExitoPct)}
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Efectividad en adjudicación</div>
                </div>

                <div style={{ background: 'var(--bg-card)', padding: '12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Monto Ganado Total</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent-green)' }}>
                    {formatearCLP(metricas.montoGanado)}
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>En adjudicaciones firmes</div>
                </div>

                <div style={{ background: 'var(--bg-card)', padding: '12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Proyectos Activos</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent-blue)' }}>
                    {metricas.proyectosActivos}
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>En ejecución hoy</div>
                </div>
              </div>

              {/* Directorio de Contrapartes / Funcionarios */}
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div>
                    <h4 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>
                      Directorio de Contrapartes Clave ({contactos.length})
                    </h4>
                    <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '2px 0 0' }}>
                      Funcionarios que firman recepciones de hitos y gestionan decretos de pago.
                    </p>
                  </div>
                  <button type="button" className="btn btn-primary btn-sm" onClick={alAbrirCrearContacto}>
                    <IconoMas size={13} />
                    Agregar Contacto
                  </button>
                </div>

                {contactos.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '16px', color: 'var(--text-muted)', fontSize: 12 }}>
                    No hay contactos registrados todavía para este organismo. Agrega el primer funcionario clave con el botón superior.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
                    {contactos.map((c) => (
                      <div
                        key={c.id}
                        style={{
                          background: 'var(--bg-root)',
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 'var(--radius-sm)',
                          padding: '12px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          gap: 10,
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>
                            {c.nombre}
                          </div>
                          <div style={{ fontSize: 11.5, color: 'var(--accent-blue)', marginBottom: 6 }}>
                            {c.cargo || 'Funcionario Público'}
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11.5, color: 'var(--text-secondary)' }}>
                            {c.telefono && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>📞 {c.telefono}</span>
                              </div>
                            )}
                            {c.email && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>✉️ {c.email}</span>
                              </div>
                            )}
                            {c.notas && (
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, fontStyle: 'italic' }}>
                                &quot;{c.notas}&quot;
                              </div>
                            )}
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderTop: '1px solid var(--border-subtle)', paddingTop: 8 }}>
                          {c.telefono && (
                            <a
                              href={obtenerUrlWhatsApp(c.telefono, base.nombre)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-secondary btn-sm"
                              style={{ padding: '3px 8px', fontSize: 11, color: '#25D366' }}
                              title="Contactar vía WhatsApp"
                            >
                              💬 WhatsApp
                            </a>
                          )}
                          <button
                            type="button"
                            className="btn-icono"
                            onClick={() => alAbrirEditarContacto(c)}
                            title="Editar contacto"
                            style={{ marginLeft: 'auto' }}
                          >
                            <IconoEditar size={13} />
                          </button>
                          <button
                            type="button"
                            className="btn-icono"
                            onClick={() => void eliminarContacto(c)}
                            title="Eliminar contacto"
                            style={{ color: 'var(--accent-red)' }}
                          >
                            <IconoBasura size={13} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Historial de Licitaciones */}
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>Historial de Licitaciones ({licitaciones.length})</h4>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      alCerrar();
                      navegar(`/nueva-oferta?organismoId=${base.id}`);
                    }}
                  >
                    <IconoRayo size={13} style={{ color: 'var(--accent-yellow)' }} />
                    Nueva Postulación
                  </button>
                </div>

                {licitaciones.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '14px', color: 'var(--text-muted)', fontSize: 12 }}>
                    No hay licitaciones registradas aún con esta institución.
                  </div>
                ) : (
                  <div className="tabla-contenedor" style={{ maxHeight: 240, overflowY: 'auto' }}>
                    <table className="tabla" style={{ fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th>Código MP</th>
                          <th>Título</th>
                          <th>Estado</th>
                          <th style={{ textAlign: 'right' }}>Monto Adjudicado</th>
                          <th>Fecha</th>
                        </tr>
                      </thead>
                      <tbody>
                        {licitaciones.map((lic) => (
                          <tr key={lic.id}>
                            <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{lic.codigoMercadoPublico}</td>
                            <td>{lic.titulo || (lic as { nombre?: string }).nombre || 'Licitación pública'}</td>
                            <td>
                              <Insignia estado={lic.estado} />
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 700, color: lic.estado === 'ADJUDICADA' ? 'var(--accent-green)' : undefined }}>
                              {lic.montoAdjudicadoNeto > 0 ? formatearCLP(lic.montoAdjudicadoNeto) : '—'}
                            </td>
                            <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                              {lic.fechaCierrePostulacion ? String(lic.fechaCierrePostulacion).slice(0, 10) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Proyectos Asociados */}
              {proyectos.length > 0 && (
                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: 16 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Proyectos en Cartera ({proyectos.length})</h4>
                  <div className="tabla-contenedor">
                    <table className="tabla" style={{ fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th>Código</th>
                          <th>Nombre Proyecto</th>
                          <th>Estado</th>
                          <th style={{ textAlign: 'right' }}>Monto Contrato</th>
                          <th>Fecha Inicio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {proyectos.map((p) => (
                          <tr key={p.id}>
                            <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{p.codigo}</td>
                            <td style={{ fontWeight: 600 }}>{p.nombre}</td>
                            <td>
                              <Insignia estado={p.estado} />
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 700 }}>
                              {formatearCLP(p.montoContratoNeto)}
                            </td>
                            <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                              {p.fechaInicio ? String(p.fechaInicio).slice(0, 10) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Notas Comerciales */}
              {base.notas && (
                <div style={{ background: 'var(--bg-surface)', padding: 14, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, fontWeight: 600 }}>
                    Notas Comerciales & Cultura del Organismo:
                  </div>
                  <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-primary)', lineHeight: 1.55 }}>
                    {base.notas}
                  </p>
                </div>
              )}
            </>
          ) : (
            /* ── PESTAÑA B: COMPORTAMIENTO FINANCIERO & FACTURACIÓN ── */
            <>
              {/* Tarjetas de Métricas de Cobranza */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                <div style={{ background: 'var(--bg-card)', padding: '12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>DSO Real (Días de Pago)</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: semaforo.color }}>
                    {semaforo.icono} {metricas.diasPromedioPago !== null ? `${Math.round(metricas.diasPromedioPago)} días` : 'Sin datos'}
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                    {metricas.sobreplazoDias && metricas.sobreplazoDias > 0
                      ? `+${metricas.sobreplazoDias} días sobre condición pactada`
                      : 'Pagos dentro del plazo'}
                  </div>
                </div>

                <div style={{ background: 'var(--bg-card)', padding: '12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Total Facturado</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>
                    {formatearCLP(metricas.totalFacturado)}
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Monto bruto en documentos SII</div>
                </div>

                <div style={{ background: 'var(--bg-card)', padding: '12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Total Cobrado / Pagado</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent-green)' }}>
                    {formatearCLP(metricas.totalCobrado)}
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                    {metricas.facturasPagadas} facturas canceladas
                  </div>
                </div>

                <div style={{ background: 'var(--bg-card)', padding: '12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Saldo Pendiente de Cobro</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: metricas.saldoPendiente > 0 ? 'var(--accent-yellow)' : 'var(--text-primary)' }}>
                    {formatearCLP(metricas.saldoPendiente)}
                  </div>
                  <div style={{ fontSize: 10.5, color: metricas.mora60d > 0 ? 'var(--accent-red)' : 'var(--text-muted)' }}>
                    {metricas.mora60d > 0 ? `⚠️ Mora +60d: ${formatearCLP(metricas.mora60d)}` : 'Sin mora crítica'}
                  </div>
                </div>
              </div>

              {/* Comparativa Visual de Plazos */}
              <div style={{ background: 'var(--bg-root)', padding: 14, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, fontSize: 12 }}>
                  <span style={{ fontWeight: 600 }}>Diagnóstico de Liquidez & Factoring:</span>
                  <span style={{ color: semaforo.color, fontWeight: 700 }}>{semaforo.etiqueta}</span>
                </div>
                <p style={{ fontSize: 11.5, color: 'var(--text-secondary)', margin: '0 0 10px', lineHeight: 1.5 }}>
                  {metricas.diasPromedioPago === null
                    ? 'Aún no registramos facturas pagadas con este organismo. Para nuevas licitaciones, se aconseja considerar la tasa promedio de instituciones de su tipo.'
                    : metricas.diasPromedioPago > 60
                      ? '⚠️ Atención Finanzas: Este organismo suele tardar más de dos meses en pagar. Es indispensable incluir el costo financiero de factoring o capital de trabajo al postular ofertas.'
                      : 'Institución con buen récord de pago institucional. El flujo de cobranza se mantiene dentro de los márgenes previstos de caja.'}
                </p>
              </div>

              {/* Historial de Facturas Emitidas */}
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Historial de Facturas SII ({facturas.length})</h4>

                {facturas.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '16px', color: 'var(--text-muted)', fontSize: 12 }}>
                    No se registran facturas emitidas para este comprador.
                  </div>
                ) : (
                  <div className="tabla-contenedor" style={{ maxHeight: 280, overflowY: 'auto' }}>
                    <table className="tabla" style={{ fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th>Folio / N°</th>
                          <th>Proyecto</th>
                          <th>Emisión</th>
                          <th>Vencimiento</th>
                          <th style={{ textAlign: 'right' }}>Total</th>
                          <th style={{ textAlign: 'right' }}>Cobrado</th>
                          <th style={{ textAlign: 'right' }}>Saldo</th>
                          <th>Situación</th>
                        </tr>
                      </thead>
                      <tbody>
                        {facturas.map((f) => {
                          const sit = f.situacion;
                          return (
                            <tr key={f.id}>
                              <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
                                {f.folioSii ? `#${f.folioSii}` : f.numero ? `#${f.numero}` : 'Sin folio'}
                              </td>
                              <td>{f.proyecto?.nombre ?? 'Gasto / Servicio'}</td>
                              <td style={{ fontSize: 11 }}>{f.fechaEmision ? String(f.fechaEmision).slice(0, 10) : '—'}</td>
                              <td style={{ fontSize: 11 }}>{f.fechaVencimiento ? String(f.fechaVencimiento).slice(0, 10) : '—'}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatearCLP(f.montoTotal)}</td>
                              <td style={{ textAlign: 'right', color: 'var(--accent-green)' }}>
                                {sit ? formatearCLP(sit.montoPagado) : '—'}
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 700, color: sit && sit.saldo > 0 ? 'var(--accent-red)' : 'var(--text-muted)' }}>
                                {sit ? formatearCLP(sit.saldo) : '—'}
                              </td>
                              <td>
                                {sit ? (
                                  sit.saldo === 0 ? (
                                    <span className="badge" style={{ background: 'var(--accent-green-soft)', color: 'var(--accent-green)' }}>
                                      Pagada
                                    </span>
                                  ) : sit.estaVencida ? (
                                    <span className="badge" style={{ background: 'var(--accent-red-soft)', color: 'var(--accent-red)' }}>
                                      Vencida (+{sit.diasAtraso}d)
                                    </span>
                                  ) : (
                                    <span className="badge" style={{ background: 'var(--accent-blue-soft)', color: 'var(--accent-blue)' }}>
                                      Al día
                                    </span>
                                  )
                                ) : (
                                  f.estado
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Pie del Modal */}
        <div className="modal-footer" style={{ justifyContent: 'space-between', padding: '14px 24px' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              alCerrar();
              navegar(`/nueva-oferta?organismoId=${base.id}`);
            }}
          >
            <IconoRayo size={13} style={{ color: 'var(--accent-yellow)' }} />
            Preparar Oferta para {base.nombre}
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={alCerrar}>
            Cerrar Ficha
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Subcomponente: Modal Crear / Editar Organismo
// ─────────────────────────────────────────────────────────────

interface PropsModalOrganismo {
  organismo: Organismo | null;
  alCerrar: () => void;
  alGuardar: (datos: Partial<CrearOrganismoInput>) => Promise<void>;
  guardando: boolean;
}

function ModalFormOrganismo({ organismo, alCerrar, alGuardar, guardando }: PropsModalOrganismo) {
  const [nombre, setNombre] = useState(organismo?.nombre ?? '');
  const [rut, setRut] = useState(organismo?.rut ?? '');
  const [tipo, setTipo] = useState<TipoOrganismo>((organismo?.tipo as TipoOrganismo) ?? 'MUNICIPALIDAD');
  const [region, setRegion] = useState(organismo?.region ?? '');
  const [comuna, setComuna] = useState(organismo?.comuna ?? '');
  const [direccion, setDireccion] = useState(organismo?.direccion ?? '');
  const [sitioWeb, setSitioWeb] = useState(organismo?.sitioWeb ?? '');
  const [notas, setNotas] = useState(organismo?.notas ?? '');

  const rutLimpio = rut.trim();
  const rutValido = rutLimpio === '' || esRutValido(rutLimpio);

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombre.trim()) return;
    if (!rutValido) return;

    await alGuardar({
      nombre: nombre.trim(),
      rut: rutLimpio || undefined,
      tipo,
      region: region.trim() || undefined,
      comuna: comuna.trim() || undefined,
      direccion: direccion.trim() || undefined,
      sitioWeb: sitioWeb.trim() || undefined,
      notas: notas.trim() || undefined,
    });
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && alCerrar()}
      role="dialog"
      aria-modal="true"
      style={{ zIndex: 1050 }}
    >
      <div className="modal" style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <h2>{organismo ? 'Editar Organismo Comprador' : 'Registrar Nuevo Organismo'}</h2>
          <button type="button" className="btn-icono" onClick={alCerrar} aria-label="Cerrar modal">
            <IconoCerrar size={15} />
          </button>
        </div>

        <form onSubmit={(e) => void enviar(e)}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="form-group">
              <label htmlFor="org-nombre">
                Razón Social / Nombre Oficial <span style={{ color: 'var(--accent-red)' }}>*</span>
              </label>
              <input
                id="org-nombre"
                required
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Ej: Ilustre Municipalidad de Providencia"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              <div className="form-group">
                <label htmlFor="org-rut">RUT Institucional</label>
                <input
                  id="org-rut"
                  value={rut}
                  onChange={(e) => setRut(e.target.value)}
                  onBlur={() => {
                    if (rutLimpio && esRutValido(rutLimpio)) {
                      setRut(formatearRut(rutLimpio));
                    }
                  }}
                  placeholder="Ej: 69.070.300-8"
                  style={{
                    borderColor: !rutValido ? 'var(--accent-red)' : undefined,
                  }}
                />
                {!rutValido ? (
                  <span style={{ fontSize: 11, color: 'var(--accent-red)' }}>
                    RUT inválido. Revisa el dígito verificador.
                  </span>
                ) : rutLimpio !== '' ? (
                  <span style={{ fontSize: 10.5, color: 'var(--accent-green)' }}>
                    ✓ RUT válido ({formatearRut(rutLimpio)})
                  </span>
                ) : (
                  <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                    Opcional. Formato: 12.345.678-K
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="org-tipo">Tipo de Institución</label>
                <select
                  id="org-tipo"
                  value={tipo}
                  onChange={(e) => setTipo(e.target.value as TipoOrganismo)}
                >
                  {TIPOS_ORGANISMO.map((t) => (
                    <option key={t} value={t}>
                      {ETIQUETAS_TIPO_ORGANISMO[t]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              <div className="form-group">
                <label htmlFor="org-region">Región</label>
                <input
                  id="org-region"
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  placeholder="Ej: Región Metropolitana"
                />
              </div>

              <div className="form-group">
                <label htmlFor="org-comuna">Comuna</label>
                <input
                  id="org-comuna"
                  value={comuna}
                  onChange={(e) => setComuna(e.target.value)}
                  placeholder="Ej: Providencia"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="org-direccion">Dirección Comercial (Exigida por el SII)</label>
              <input
                id="org-direccion"
                value={direccion}
                onChange={(e) => setDireccion(e.target.value)}
                placeholder="Ej: Pedro de Valdivia 963"
              />
            </div>

            <div className="form-group">
              <label htmlFor="org-sitio">Sitio Web / Portal</label>
              <input
                id="org-sitio"
                type="url"
                value={sitioWeb}
                onChange={(e) => setSitioWeb(e.target.value)}
                placeholder="https://www.providencia.cl"
              />
            </div>

            <div className="form-group">
              <label htmlFor="org-notas">Notas Comerciales o de Adjudicación</label>
              <textarea
                id="org-notas"
                rows={3}
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                placeholder="Observaciones sobre criterios de evaluación, exigencias técnicas o fechas habituales de decretos de pago."
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={alCerrar} disabled={guardando}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={guardando || !nombre.trim() || !rutValido}
            >
              {guardando ? 'Guardando…' : organismo ? 'Guardar Cambios' : 'Crear Organismo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Subcomponente: Modal Crear / Editar Contacto
// ─────────────────────────────────────────────────────────────

interface PropsModalContacto {
  organismoId: string;
  contacto?: ContactoOrganismo;
  alCerrar: () => void;
}

function ModalFormContacto({ organismoId, contacto, alCerrar }: PropsModalContacto) {
  const avisos = useAvisos();
  const crearMut = useCrearContactoOrganismo();
  const actualizarMut = useActualizarContactoOrganismo();

  const [nombre, setNombre] = useState(contacto?.nombre ?? '');
  const [cargo, setCargo] = useState(contacto?.cargo ?? '');
  const [telefono, setTelefono] = useState(contacto?.telefono ?? '');
  const [email, setEmail] = useState(contacto?.email ?? '');
  const [notas, setNotas] = useState(contacto?.notas ?? '');

  const guardando = crearMut.isPending || actualizarMut.isPending;

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombre.trim()) return;

    try {
      if (contacto) {
        await actualizarMut.mutateAsync({
          id: contacto.id,
          datos: {
            nombre: nombre.trim(),
            cargo: cargo.trim() || undefined,
            telefono: telefono.trim() || undefined,
            email: email.trim() || undefined,
            notas: notas.trim() || undefined,
          },
        });
        avisos.exito(`Contacto "${nombre}" actualizado.`);
      } else {
        await crearMut.mutateAsync({
          organismoId,
          datos: {
            nombre: nombre.trim(),
            cargo: cargo.trim() || undefined,
            telefono: telefono.trim() || undefined,
            email: email.trim() || undefined,
            notas: notas.trim() || undefined,
          },
        });
        avisos.exito(`Contacto "${nombre}" registrado con éxito.`);
      }
      alCerrar();
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'Error al guardar contacto.');
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && alCerrar()}
      role="dialog"
      aria-modal="true"
      style={{ zIndex: 1050 }}
    >
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2>{contacto ? 'Editar Contacto' : 'Agregar Contraparte Clave'}</h2>
          <button type="button" className="btn-icono" onClick={alCerrar} aria-label="Cerrar modal">
            <IconoCerrar size={15} />
          </button>
        </div>

        <form onSubmit={(e) => void enviar(e)}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label htmlFor="contacto-nombre">
                Nombre Completo <span style={{ color: 'var(--accent-red)' }}>*</span>
              </label>
              <input
                id="contacto-nombre"
                required
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Ej: Carlos Soto"
              />
            </div>

            <div className="form-group">
              <label htmlFor="contacto-cargo">Cargo / Función</label>
              <input
                id="contacto-cargo"
                value={cargo}
                onChange={(e) => setCargo(e.target.value)}
                placeholder="Ej: Jefe de Proyectos TI / Administrador de Contrato"
              />
            </div>

            <div className="form-group">
              <label htmlFor="contacto-tel">Teléfono / WhatsApp Corporativo</label>
              <input
                id="contacto-tel"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                placeholder="+56 9 1234 5678"
              />
            </div>

            <div className="form-group">
              <label htmlFor="contacto-email">Correo Electrónico</label>
              <input
                id="contacto-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="csoto@organismo.gob.cl"
              />
            </div>

            <div className="form-group">
              <label htmlFor="contacto-notas">Notas o Instrucciones</label>
              <textarea
                id="contacto-notas"
                rows={2}
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                placeholder="Ej: Firma las actas de recepción conforme los viernes por la mañana."
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={alCerrar} disabled={guardando}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={guardando || !nombre.trim()}>
              <IconoCheck size={14} />
              {guardando ? 'Guardando…' : contacto ? 'Guardar Cambios' : 'Registrar Contacto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
